import {
  ActionSheetWrapper,
  Accordion,
  AccordionSummary,
  ActionSheetItem,
  Button,
  Icon,
  ListItemText,
  Status,
  StatusState,
  StatusVariant,
  theme,
  CustomIconName,
  AccordionDetails,
} from '@ketch-com/deck'
import { GetConsentStatusResponseBodyPurposesItemDTO } from '@ketch-com/figurehead'
import { Box, styled, Tooltip, Typography } from '@mui/material'
import { AuditLogActionVariant } from 'pages/insights/auditLog/components/AuditLogPopup'
import { ListItemCards } from 'pages/insights/auditLog/components/consentsTab/components/PurposeListItem/components/ListItemCards'
import { ListItemConnections } from 'pages/insights/auditLog/components/consentsTab/components/PurposeListItem/components/ListItemConnections'
import { useAuditLogContext } from 'pages/insights/auditLog/context/AuditLogContext'
import React, { useCallback, useState } from 'react'

type PurposeListItemProps = {
  purpose: GetConsentStatusResponseBodyPurposesItemDTO
}

const PurposeSummaryLeftBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
})

const PurposeSummaryRightBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 32,
})

interface OptionItem {
  label: string
  action: AuditLogActionVariant
  icon?: CustomIconName
  tooltip?: string
}

const options: OptionItem[] = [
  {
    label: 'Opt In',
    action: AuditLogActionVariant.AcceptSingleConsent,
    icon: 'OCheck',
  },
  {
    label: 'Opt Out',
    action: AuditLogActionVariant.RejectSingleConsent,
    icon: 'OCross',
  },
  {
    label: 'Reset to Default',
    action: AuditLogActionVariant.SetDefaultSingleConsent,
    tooltip:
      'This will set this purpose to its default state based on its legal basis in the jurisdiction which the consent record originated.',
  },
]

export const PurposeListItem: React.FC<PurposeListItemProps> = ({ purpose }) => {
  const { triggerAuditLogPopup } = useAuditLogContext()

  const [expanded, setExpanded] = useState(false)

  // Options action menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const actionMenuOpen = Boolean(anchorEl)

  // Options action menu handlers - we stop propagation in each so the clicks don't also open/close the accordion
  const handleButtonClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
  }, [])

  const handleActionMenuItemClick = useCallback(
    (e: React.MouseEvent<HTMLElement>, index?: number) => {
      e.stopPropagation()
      setAnchorEl(null)
      if (index !== undefined && purpose.code) {
        const option = options[index]
        triggerAuditLogPopup({ variant: option.action, purposeCode: purpose.code })
      }
    },
    [triggerAuditLogPopup, purpose.code],
  )

  const handleActionMenuClose = useCallback((e: any) => {
    e.stopPropagation()
    setAnchorEl(null)
  }, [])

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      color={expanded ? theme.palette.White.o100 : theme.palette.Black.o4}
      sx={{
        border: `1px solid ${theme.palette.Common.Divider}`,
      }}
    >
      <AccordionSummary onExpandIconClick={() => setExpanded(expanded => !expanded)} sx={{ height: '67px' }}>
        <PurposeSummaryLeftBox>
          <Typography fontSize={16} fontWeight={600}>
            {purpose?.name}
          </Typography>
          <Typography color={theme.palette.Text.Secondary}>{purpose?.code}</Typography>
        </PurposeSummaryLeftBox>
        <PurposeSummaryRightBox>
          <Status
            variant={StatusVariant.filled}
            status={purpose?.allowed ? StatusState.success : StatusState.draft}
            label={purpose?.allowed ? 'Opted In' : 'Opted Out'}
            icon={purpose?.allowed ? 'OCheck' : 'ODecline'}
          />
          <Box>
            <Button variant="icon" size="medium" color="tertiary" onClick={handleButtonClick}>
              <Icon name="OMore" />
            </Button>
            <ActionSheetWrapper anchorEl={anchorEl} open={actionMenuOpen} onClose={e => handleActionMenuClose(e)}>
              {options.map((option, index) => (
                <Tooltip title={option.tooltip} placement="bottom" key={`${option.label}-${option.action}`}>
                  <span>
                    <ActionSheetItem key={option.label} onClick={event => handleActionMenuItemClick(event, index)}>
                      <ListItemText>{option.label}</ListItemText>
                    </ActionSheetItem>
                  </span>
                </Tooltip>
              ))}
            </ActionSheetWrapper>
          </Box>
        </PurposeSummaryRightBox>
      </AccordionSummary>
      <AccordionDetails>
        <ListItemCards purpose={purpose} />
        <ListItemConnections purpose={purpose} />
      </AccordionDetails>
    </Accordion>
  )
}
